import { useNavigate, useSearch } from "@tanstack/react-router";
import type { CreatorActivity } from "@unbox/api";
import {
  Activity,
  ArrowDown,
  Building07,
  ChevronDown,
  ClockRefresh,
  EyeOff,
  FilterLines,
  Mail01,
  MessageTextSquare01,
  MessageXSquare,
  SearchMd,
  User01,
  XClose,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ActivityFilter,
  useCandidateActivities,
  useConversations,
  useCreatorActivities,
  useCreatorsWithActivities,
  useMessageCreators,
} from "@/api/message-creator";
import { useStoreComposerImage } from "@/api/message-images";
import { ActivityDaySection } from "@/components/activities/activity-day-section";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Tabs } from "@/components/application/tabs/tabs";
import { LoadingPageState } from "@/components/loading-page-state";
import { type ComposerImage, releaseComposerImage } from "@/components/message-composer/composer-image";
import { SAMPLE_PREVIEW_CONTEXT } from "@/components/message-composer/composer-preview";
import { toastImageUploadFailed } from "@/components/message-composer/composer-toasts";
import { MessageComposer } from "@/components/message-composer/message-composer";
import { PageLayout } from "@/components/page-layout";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMerchantName } from "@/hooks/use-merchant-name";
import { CandidateDetails } from "@/pages/creators/candidate-details";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { useAuth } from "@/providers/auth-provider";
import { useMerchant } from "@/providers/MerchantProvider";
import type { interactionsRoute } from "@/router";
import { cx } from "@/utils/cx";
import { formatActivityDate, groupActivitiesByDay } from "@/utils/date-grouping";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { extractTextMessageContent } from "@/utils/message-content";
import { getUserInitials } from "@/utils/user-initials";
import { MessagesActivityEmptyState } from "./messages-activity-empty-state";

// Filter options for the left panel
const FILTER_OPTIONS: {
  value: ActivityFilter;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  hasSeparator?: boolean;
}[] = [
  { value: "all", label: "All Activities", icon: Activity, hasSeparator: true },
  { value: "messages", label: "Messages", icon: Mail01 },
  { value: "creator_activity", label: "Creator activity", icon: User01 },
  { value: "brand_activity", label: "Brand activity", icon: Building07 },
];

// Activity type tabs for the right panel (per creator)
const activityTabs = [
  { id: "all" as const, label: "All Activities" },
  { id: "messages" as const, label: "Messages" },
  { id: "activities" as const, label: "Activities" },
];

// Helper to get activity type label or message preview
const getActivityTypeLabel = (activity: CreatorActivity): string => {
  switch (activity.type) {
    case "message": {
      // Get message preview
      let messagePreview = "";

      if (activity.messageBody.type === "TEXT") {
        messagePreview = extractTextMessageContent(activity.messageBody.content);
      } else if (activity.messageBody.type === "IMAGE") {
        messagePreview = "[Image]";
      } else if (activity.messageBody.type === "PRODUCT_CARD") {
        messagePreview = "[Product Card]";
      } else if (activity.messageBody.type === "TARGET_COLLABORATION_CARD") {
        messagePreview = "[Collaboration Invite]";
      } else {
        messagePreview = "[Message]";
      }

      // Truncate to 50 characters
      const maxLength = 50;
      if (messagePreview.length > maxLength) {
        messagePreview = `${messagePreview.substring(0, maxLength)}...`;
      }

      // Add "You: " prefix for sent messages
      if (activity.direction === "sent") {
        return `You: ${messagePreview}`;
      }

      return messagePreview;
    }
    case "video_post":
      return "Video Posted";
    case "collab_join":
      return "Accepted Collaboration";
    case "collab_removal":
      return "Removed from Collaboration";
    case "safety_event":
      switch (activity.actionType) {
        case "video_demonetized":
          return "Video Demonetized";
        case "video_removed":
          return "Video Removed";
      }
      return "Safety Action";
    case "sample":
      return "Sample Approved"; // This will vary based on displayStatus, but default to this
    case "gmv_max":
      return "Invited to Collaboration";
    default:
      return "Activity";
  }
};

// Helper to map left panel filter to right panel tab
const getTabFromFilter = (filter: ActivityFilter): "all" | "messages" | "activities" => {
  switch (filter) {
    case "all":
      return "all";
    case "messages":
      return "messages";
    case "creator_activity":
    case "brand_activity":
      return "activities";
    default:
      return "all";
  }
};

// Loading placeholder for the right panel while the feed is fetching, so we never
// flash the "No activity yet" empty state during load (UNB-2097).
const RightPanelSkeleton = () => (
  <div className="flex-1 bg-unbox-cream-50 flex flex-col p-6 gap-5">
    {["s1", "s2", "s3", "s4", "s5"].map((key) => (
      <div key={key} className="flex gap-3 w-full max-w-[550px] mx-auto">
        <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
        <Skeleton className="h-16 flex-1 rounded-lg" />
      </div>
    ))}
  </div>
);

export const Conversations = () => {
  // Get URL params for activity filter
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof interactionsRoute)["useSearch"]>;
  const navigate = useNavigate() as ReturnType<(typeof interactionsRoute)["useNavigate"]>;
  const selectedFilter = (urlParams.activityType || "all") as ActivityFilter;

  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
  const [selectedCreatorName, setSelectedCreatorName] = useState<string | null>(null);
  const [selectedCreatorType, setSelectedCreatorType] = useState<"creator" | "candidate" | null>(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [creatorActivityTab, setCreatorActivityTab] = useState<"all" | "messages" | "activities">("all");
  const [messageText, setMessageText] = useState("");
  const [messageImage, setMessageImage] = useState<ComposerImage | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [profileModalCreatorId, setProfileModalCreatorId] = useState<string | null>(null);
  const [profileModalCandidateId, setProfileModalCandidateId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const activitiesContainerRef = useRef<HTMLDivElement>(null);
  const creatorActivitiesContainerRef = useRef<HTMLDivElement>(null);
  const previousCreatorIdRef = useRef<string | null>(null);
  // Bounds the auto-fill loop (loads pages until the panel is scrollable) so a
  // creator with sparse early pages can't trigger unbounded fetching.
  const autoFillCountRef = useRef(0);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get current user and merchant for brand avatar
  const { user } = useAuth();
  const currentMerchantId = useMerchant();

  // Get merchant logo for brand activities
  const brandAvatar = useMemo(() => {
    if (!user?.merchants || !currentMerchantId) return null;
    const currentMerchant = user.merchants.find((m) => m.id === currentMerchantId);
    return currentMerchant?.logoUrl || null;
  }, [user?.merchants, currentMerchantId]);

  // Fetch creators with their last activity (filtered by backend)
  const {
    data: creatorsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCreatorsWithActivities(selectedFilter, debouncedSearchQuery);

  // Fetch conversations to get conversation IDs for messaging
  // Only fetch when a creator is selected (to avoid unnecessary API calls and validation errors for internal users on client merchants)
  const { data: conversationsData } = useConversations(undefined, { enabled: !!selectedCreatorId });

  // Fetch activities for the selected creator/candidate (with filter).
  // Candidates are NOT in brand_creators, so the creator endpoint returns
  // `isNewCreator` and drops all DB activities (videos, collabs, ...). Route
  // candidate rows to the candidate endpoint, mirroring the profile modal, so
  // their full activity shows (UNB-2097).
  const isCandidate = selectedCreatorType === "candidate";

  const creatorActivitiesQuery = useCreatorActivities(
    isCandidate ? undefined : selectedCreatorName || undefined,
    creatorActivityTab,
  );
  const candidateActivitiesQuery = useCandidateActivities(
    isCandidate ? selectedCreatorId || undefined : undefined,
    creatorActivityTab,
  );

  const {
    data: creatorActivitiesData,
    isLoading: creatorActivitiesLoading,
    fetchNextPage: fetchNextCreatorPage,
    hasNextPage: hasNextCreatorPage,
    isFetchingNextPage: isFetchingNextCreatorPage,
  } = isCandidate ? candidateActivitiesQuery : creatorActivitiesQuery;

  // Send message mutation
  const { mutateAsync: sendMessage, isPending: isSendingMessage } = useMessageCreators();

  const { store: storeImage, isPending: isStoringImage } = useStoreComposerImage();
  const isSending = isSendingMessage || isStoringImage;
  const merchantName = useMerchantName();

  // Flatten creators from all pages
  const allCreators = useMemo(() => {
    if (!creatorsData?.pages) return [];
    return creatorsData.pages.flatMap((page) => page.creators);
  }, [creatorsData?.pages]);

  // Get selected creator's avatar
  const selectedCreatorAvatar = useMemo(() => {
    if (!selectedCreatorId) return null;
    const creator = allCreators.find((c) => c.creatorId === selectedCreatorId);
    return creator?.avatar || null;
  }, [selectedCreatorId, allCreators]);

  // Get selected creator's display name (fallback to username)
  const selectedCreatorDisplayName = useMemo(() => {
    if (!selectedCreatorId) return null;
    const creator = allCreators.find((c) => c.creatorId === selectedCreatorId);
    return creator?.displayName || creator?.username || null;
  }, [selectedCreatorId, allCreators]);

  // Apply sort order to creators
  const filteredCreators = useMemo(() => {
    let creators = allCreators;

    // Apply sort order
    if (sortOrder === "oldest") {
      creators = [...creators].reverse();
    }

    return creators;
  }, [allCreators, sortOrder]);

  // Flatten creator activities from all pages
  // Activities are in newest-first order (feed-style, not chat-style)
  const creatorActivities = useMemo(() => {
    if (!creatorActivitiesData?.pages) return [];
    return creatorActivitiesData.pages.flatMap((page) => page.activities);
  }, [creatorActivitiesData?.pages]);

  // Filter creator activities by tab
  const filteredCreatorActivities = useMemo(() => {
    switch (creatorActivityTab) {
      case "messages":
        return creatorActivities.filter((a) => a.type === "message");
      case "activities":
        return creatorActivities.filter((a) => a.type !== "message");
      default:
        return creatorActivities;
    }
  }, [creatorActivityTab, creatorActivities]);

  // Group activities by day for collapsible sections
  const groupedActivities = useMemo(() => {
    const grouped = groupActivitiesByDay(filteredCreatorActivities);
    // Convert to array and sort by date (newest first)
    return Array.from(grouped.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [filteredCreatorActivities]);

  // Get conversation ID for the selected creator
  useEffect(() => {
    if (!selectedCreatorName || !conversationsData) {
      setConversationId(null);
      return;
    }

    // Find the conversation for this creator
    const allConversations = conversationsData.pages?.flatMap((page) => page.conversations) || [];
    const conversation = allConversations.find((c) => c.username === selectedCreatorName);

    setConversationId(conversation?.id || null);
  }, [selectedCreatorName, conversationsData]);

  const handleSendMessage = async () => {
    if ((!messageText.trim() && !messageImage) || !selectedCreatorId || !selectedCreatorName) return;

    let imageId: string | null;
    try {
      imageId = await storeImage(messageImage);
    } catch {
      toastImageUploadFailed();
      return;
    }
    await sendMessage({
      creatorIds: [selectedCreatorId],
      message: messageText.trim(),
      conversationId: conversationId || undefined,
      imageId: imageId ?? undefined,
    });
    setMessageText("");
    releaseComposerImage(messageImage);
    setMessageImage(null);
  };

  useEffect(() => {
    setMessageImage((current) => {
      releaseComposerImage(current);
      return null;
    });
  }, [selectedCreatorId]);

  // Reset selected creator when filter changes and sync tab with filter
  useEffect(() => {
    setSelectedCreatorId(null);
    setSelectedCreatorName(null);
    setSelectedCreatorType(null);
    setCreatorActivityTab(getTabFromFilter(selectedFilter));
  }, [selectedFilter]);

  // Reset selected creator when search query changes (debounced)
  useEffect(() => {
    setSelectedCreatorId(null);
    setSelectedCreatorName(null);
  }, [debouncedSearchQuery]);

  // Auto-select first creator when list loads or changes (after loading completes)
  // This ensures we select the first creator after the query finishes
  useEffect(() => {
    // Wait for loading to complete and ensure we have creators
    if (!isLoading && filteredCreators.length > 0 && !selectedCreatorId) {
      const firstCreator = filteredCreators[0];
      setSelectedCreatorId(firstCreator.creatorId);
      setSelectedCreatorName(firstCreator.username);
      setSelectedCreatorType(firstCreator.type);
    }
  }, [filteredCreators, selectedCreatorId, isLoading]);

  // Auto-scroll to top when creator changes or new activity is added (feed-style)
  useEffect(() => {
    const container = creatorActivitiesContainerRef.current;
    if (!container || filteredCreatorActivities.length === 0) return;

    const previousCreatorId = previousCreatorIdRef.current;
    const isCreatorChanged = previousCreatorId !== selectedCreatorId;

    // If creator changed, scroll to top to show newest activities
    if (isCreatorChanged) {
      container.scrollTop = 0;
      previousCreatorIdRef.current = selectedCreatorId;
      return;
    }

    // For new activities (messages sent), scroll to top to show them
    // This happens automatically when the query refetches
    const isAtTop = container.scrollTop < 50;
    if (isAtTop) {
      container.scrollTop = 0;
    }
  }, [filteredCreatorActivities, selectedCreatorId]);

  // Handle scroll to load more activities in left panel
  const handleActivitiesScroll = useCallback(() => {
    const container = activitiesContainerRef.current;
    if (!container || isFetchingNextPage) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 100) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Handle scroll to load more creator activities in right panel
  // Load older activities when scrolling to the bottom (feed-style)
  const handleCreatorActivitiesScroll = useCallback(() => {
    const container = creatorActivitiesContainerRef.current;
    if (!container || isFetchingNextCreatorPage || !hasNextCreatorPage) return;

    // Load more when scrolling near the bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 100) {
      fetchNextCreatorPage();
    }
  }, [fetchNextCreatorPage, hasNextCreatorPage, isFetchingNextCreatorPage]);

  // Reset the auto-fill budget whenever the selected creator changes.
  useEffect(() => {
    autoFillCountRef.current = 0;
  }, [selectedCreatorId]);

  // Auto-fill short pages: DB activities (videos/collabs) often live on later
  // pages (they come after the message pages / once message history is
  // exhausted). If the first page is short, the panel isn't scrollable and the
  // scroll handler never fires — so keep fetching until it's scrollable or there
  // are no more pages (UNB-2097). Bounded to avoid runaway fetching.
  useEffect(() => {
    const container = creatorActivitiesContainerRef.current;
    if (!container || creatorActivitiesLoading) return;
    if (!hasNextCreatorPage || isFetchingNextCreatorPage) return;

    const isScrollable = container.scrollHeight > container.clientHeight + 1;
    if (!isScrollable && autoFillCountRef.current < 10) {
      autoFillCountRef.current += 1;
      fetchNextCreatorPage();
    }
  }, [
    creatorActivitiesLoading,
    hasNextCreatorPage,
    isFetchingNextCreatorPage,
    fetchNextCreatorPage,
    filteredCreatorActivities.length,
  ]);

  const selectedFilterOption = FILTER_OPTIONS.find((f) => f.value === selectedFilter) || FILTER_OPTIONS[0];
  const selectedFilterLabel = selectedFilterOption.label;
  const SelectedFilterIcon = selectedFilterOption.icon;

  // Show a loading state in the right panel while the creators list is still
  // loading, or while it has loaded but the first creator hasn't been
  // auto-selected yet — so we never show "No activity yet" during fetch. The
  // empty state only renders once loading is confirmed with no creators.
  const isRightPanelLoading = isLoading || (filteredCreators.length > 0 && !(selectedCreatorId && selectedCreatorName));

  // Genuine zero-data (no creators at all, no search, default filter) replaces
  // the whole page. A filter or search that returns nothing keeps the inline
  // "No activity yet" state below.
  const isUnfiltered = !debouncedSearchQuery && selectedFilter === "all";
  if (isUnfiltered && isLoading) return <LoadingPageState />;

  if (isUnfiltered && allCreators.length === 0) {
    return <MessagesActivityEmptyState />;
  }

  return (
    <PageLayout
      title="Messages & Activity"
      icon={MessageTextSquare01}
      subtitle="Browse all activities with creators on TikTok"
      fitViewport
      callToAction={
        <div className="relative w-[330px]">
          <SearchMd className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#717680" }} />
          <Input
            type="text"
            placeholder="Search creators"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 h-10 text-sm border-1 placeholder:text-[#717680]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <XClose className="w-4 h-4" style={{ color: "#717680" }} />
            </button>
          )}
        </div>
      }
    >
      <PageHero
        pageKey={PageHeroKey.MessagesAndActivity}
        title="One feed for every creator touchpoint."
        text1={{ icon: ClockRefresh, message: "See messages, sample requests and posts as they happen." }}
        text2={{ icon: FilterLines, message: "Filter by creator or brand activity." }}
        text3={{ icon: MessageXSquare, message: "See which creators are active, even when they're not messaging." }}
        image="/heroes/messages-and-activity.png"
      />
      {/* Panels Container */}
      <div
        className={cx("flex flex-1 min-h-0 overflow-hidden gap-4 w-full justify-center", LAYOUT_CONSTRAINTS.MIN_WIDTH)}
      >
        <div className={cx("flex flex-1 overflow-hidden gap-4", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
          {/* Left Panel - Activity Feed */}
          <div className="w-[380px] flex flex-col rounded-xl overflow-hidden">
            {/* Latest Activity Sort and Filter */}
            <div className="pb-4 flex items-center justify-between">
              {/* Latest Activity Sort Button */}
              <button
                type="button"
                onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
                className="flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors m-0"
              >
                <span>Latest Activities</span>
                <ArrowDown className={cx("w-4 h-4 transition-transform", sortOrder === "oldest" && "rotate-180")} />
              </button>

              {/* Filter Dropdown */}
              <div className="relative min-w-[200px]">
                <button
                  type="button"
                  onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white border border-border-primary rounded-lg hover:bg-unbox-cream-25 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <SelectedFilterIcon className="w-4 h-4" />
                    <span className="text-sm text-primary font-medium">{selectedFilterLabel}</span>
                  </div>
                  <ChevronDown
                    className={cx("w-4 h-4 text-quaternary transition-transform", filterDropdownOpen && "rotate-180")}
                  />
                </button>

                {filterDropdownOpen && (
                  <div className="absolute top-full right-0 z-10 mt-1 w-64 rounded-lg border border-secondary bg-primary p-1.5 shadow-lg">
                    {FILTER_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      return (
                        <div key={option.value}>
                          <button
                            type="button"
                            onClick={() => {
                              // Update URL with activity filter
                              if (option.value === "all") {
                                // Remove activityType from URL when "all" is selected
                                navigate({ search: (prev) => ({ ...prev, activityType: undefined }) });
                              } else {
                                // Add/update activityType in URL
                                navigate({ search: (prev) => ({ ...prev, activityType: option.value }) });
                              }
                              setFilterDropdownOpen(false);
                            }}
                            className={cx(
                              "flex w-full items-center gap-2 rounded-md px-2.5 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-unbox-cream-25",
                              selectedFilter === option.value ? "bg-unbox-cream-50 text-primary" : "text-secondary",
                            )}
                          >
                            <Icon
                              className={cx(
                                "size-4 shrink-0",
                                selectedFilter === option.value ? "text-fg-secondary" : "text-fg-quaternary",
                              )}
                            />
                            {option.label}
                          </button>
                          {option.hasSeparator && <div className="-mx-1.5 my-1.5 border-t border-secondary" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Creators List */}
            <div
              ref={activitiesContainerRef}
              className={cx(
                "flex-1 min-h-0 overflow-y-auto rounded-xl border border-secondary bg-primary",
                (isLoading || filteredCreators.length === 0) && "bg-unbox-cream-50 p-4",
              )}
              onScroll={handleActivitiesScroll}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingIndicator />
                </div>
              ) : filteredCreators.length > 0 ? (
                <div className="flex flex-col">
                  {filteredCreators.map((creator, index) => {
                    const isSelected = selectedCreatorId === creator.creatorId;
                    const displayName = creator.displayName || creator.username;
                    const activityLabel = getActivityTypeLabel(creator.lastActivity);
                    const timeAgo = formatActivityDate(new Date(creator.lastActivity.timestamp));

                    return (
                      <div key={creator.creatorId}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCreatorId(creator.creatorId);
                            setSelectedCreatorName(creator.username);
                            setSelectedCreatorType(creator.type);
                            setCreatorActivityTab(getTabFromFilter(selectedFilter));
                          }}
                          className={cx(
                            "w-full p-3 text-left transition-colors",
                            isSelected ? "bg-unbox-cream-50" : "hover:bg-unbox-cream-25",
                          )}
                        >
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            {creator.avatar ? (
                              <img
                                src={creator.avatar}
                                alt={displayName}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-semibold text-primary">
                                  {getUserInitials(displayName)}
                                </span>
                              </div>
                            )}

                            {/* Creator Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between">
                                <h3 className="font-semibold text-sm text-primary truncate">{displayName}</h3>
                                <span className="text-xs text-tertiary whitespace-nowrap">{timeAgo}</span>
                              </div>
                              <p className="text-xs text-secondary truncate">{activityLabel}</p>
                            </div>
                          </div>
                        </button>
                        {/* Divider between items */}
                        {index < filteredCreators.length - 1 && <div className="border-b border-border-primary" />}
                      </div>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-4">
                      <LoadingIndicator />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-sm text-tertiary">
                  {selectedFilter === "messages"
                    ? "No messages found"
                    : selectedFilter === "creator_activity"
                      ? "No creator activities found"
                      : selectedFilter === "brand_activity"
                        ? "No brand activities found"
                        : "No activities found"}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Creator Activities */}
          <div className="flex-1 flex flex-col rounded-xl overflow-hidden">
            {isRightPanelLoading ? (
              <RightPanelSkeleton />
            ) : selectedCreatorId && selectedCreatorName ? (
              <>
                {/* Creator Header with Tabs on Same Row */}
                <div className="p-4 bg-unbox-cream-50">
                  <div className="flex items-center justify-between gap-4">
                    {/* Profile Section */}
                    <div className="flex items-center gap-3">
                      {selectedCreatorAvatar ? (
                        <img
                          src={selectedCreatorAvatar}
                          alt={selectedCreatorDisplayName || selectedCreatorName || "Creator"}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-tertiary flex items-center justify-center flex-shrink-0">
                          <span className="text-base font-semibold text-primary">
                            {getUserInitials(selectedCreatorName || "")}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0 flex flex-col">
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedCreatorType === "candidate") {
                              setProfileModalCandidateId(selectedCreatorId);
                            } else {
                              setProfileModalCreatorId(selectedCreatorId);
                            }
                          }}
                          className="text-base font-semibold text-primary truncate border-b-[2px] border-dotted border-black mb-1 hover:text-brand-secondary"
                        >
                          {selectedCreatorDisplayName || selectedCreatorName}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedCreatorType === "candidate") {
                              setProfileModalCandidateId(selectedCreatorId);
                            } else {
                              setProfileModalCreatorId(selectedCreatorId);
                            }
                          }}
                          className="text-sm text-primary-gray font-medium hover:text-brand-secondary cursor-pointer w-fit"
                        >
                          See Profile
                        </button>
                      </div>
                    </div>

                    {/* Activity Type Tabs - Minimal Width */}
                    <Tabs
                      selectedKey={creatorActivityTab}
                      onSelectionChange={(key) => setCreatorActivityTab(key as typeof creatorActivityTab)}
                    >
                      <Tabs.List
                        type="button-border"
                        items={activityTabs}
                        size="sm"
                        className="bg-white ring-1"
                        style={{ borderColor: "#D5D7DA" }}
                      >
                        {(tab) => (
                          <Tabs.Item
                            {...tab}
                            className={(state) =>
                              cx(
                                "text-xs font-semibold py-1.5 px-2.5 transition-colors rounded-md whitespace-nowrap",
                                state.isSelected
                                  ? "bg-white border-2 border-black shadow-none"
                                  : "bg-white border-2 border-transparent hover:border-gray-200",
                              )
                            }
                            style={{ color: "#414651" }}
                          />
                        )}
                      </Tabs.List>
                    </Tabs>
                  </div>
                </div>

                {/* Creator Activities Content */}
                <div
                  ref={creatorActivitiesContainerRef}
                  className="flex-1 overflow-y-auto p-6 bg-unbox-cream-50 flex justify-center"
                  onScroll={handleCreatorActivitiesScroll}
                >
                  <div className="w-full max-w-[550px]">
                    {creatorActivitiesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingIndicator />
                      </div>
                    ) : groupedActivities.length > 0 ? (
                      <>
                        {/* Activities grouped by day - newest at top */}
                        {groupedActivities.map((dayGroup) => (
                          <ActivityDaySection
                            key={dayGroup.dateLabel}
                            date={dayGroup.dateLabel}
                            activities={dayGroup.activities}
                            creatorAvatar={selectedCreatorAvatar}
                            brandAvatar={brandAvatar}
                            creatorName={selectedCreatorName}
                          />
                        ))}

                        {/* Loading indicator at bottom when fetching older activities */}
                        {isFetchingNextCreatorPage && (
                          <div className="flex items-center justify-center py-4">
                            <LoadingIndicator />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center py-12 text-sm text-tertiary">
                        {creatorActivityTab === "messages" && "No messages found"}
                        {creatorActivityTab === "activities" && "No activities found"}
                        {creatorActivityTab === "all" && "No activities found"}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-unbox-cream-50 px-6 pb-6 pt-4">
                  <MessageComposer
                    key={selectedCreatorId}
                    value={messageText}
                    previewContext={{
                      ...SAMPLE_PREVIEW_CONTEXT,
                      creatorName: selectedCreatorName,
                      ...(merchantName && { shopName: merchantName }),
                    }}
                    onChange={setMessageText}
                    image={messageImage}
                    onImageChange={setMessageImage}
                    variableContext="creator-messaging"
                    emoji
                    minHeight={84}
                    disabled={isSending}
                    imageRejectedTitle="That image wasn’t added"
                    submitOnEnter
                    primaryAction={{
                      label: "Send",
                      onClick: handleSendMessage,
                      isDisabled: (!messageText.trim() && !messageImage) || isSending,
                      isLoading: isSending,
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 bg-unbox-cream-50 flex flex-col items-center justify-center px-6 py-12">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#F5F5F5]">
                    <EyeOff className="w-6 h-6 text-quaternary" />
                  </div>

                  {/* Title and Description */}
                  {selectedFilter === "messages" ? (
                    <>
                      <h3 className="text-base font-semibold text-secondary">No Messages yet</h3>
                      <p className="text-sm text-tertiary">
                        Any conversation with creators will appear here as you engage with them.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-base font-semibold text-secondary">No activity yet</h3>
                      <p className="text-sm text-tertiary">
                        Conversations, collaborations, samples, and posts <br /> will appear here as creators engage.
                      </p>
                    </>
                  )}

                  {/* Find creators button - Hide until Search feature is implemented */}
                  {/* <button
                  type="button"
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-tertiary hover:text-secondary transition-colors bg-white border border-border-primary rounded-md"
                  onClick={() => {
                    // TODO: Navigate to creator explorer or open creator search
                    console.log("Find creators clicked");
                  }}
                >
                  <SearchMd className="w-4 h-4" />
                  Find creators
                </button> */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Creator Profile Modal */}
      {profileModalCreatorId && (
        <CreatorDetails id={profileModalCreatorId} onClose={() => setProfileModalCreatorId(null)} />
      )}

      {/* Candidate Profile Modal */}
      {profileModalCandidateId && (
        <CandidateDetails id={profileModalCandidateId} onClose={() => setProfileModalCandidateId(null)} />
      )}
    </PageLayout>
  );
};
